import { useState } from 'react';
import {
  Brain,
  Sparkles,
  ShieldAlert,
  SearchCode,
  Crosshair,
  UserCheck,
  ClipboardCheck,
  Play,
  Clock,
  Zap,
  TrendingUp,
  BarChart3,
  CheckCircle,
  AlertTriangle,
  ArrowUpRight,
  Activity,
} from 'lucide-react';

// ---- Types ----

type SkillStatus = 'active' | 'idle' | 'disabled';
type ExecutionResult = 'success' | 'escalated' | 'partial';
type ExecutionTrigger = 'automated' | 'manual';

interface SkillData {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: SkillStatus;
  confidence: number;
  executionCount: number;
  avgResponseTime: number;
  accentColor: string;
  accentBg: string;
  accentBorder: string;
  accentText: string;
  progressColor: string;
}

interface SkillExecution {
  id: string;
  skillName: string;
  skillId: string;
  timestamp: string;
  duration: number;
  result: ExecutionResult;
  trigger: ExecutionTrigger;
}

// ---- Mock Data ----

const SKILLS: SkillData[] = [
  {
    id: 'alert_triage',
    name: 'Alert Triage & Enrichment',
    description:
      'Rapidly assess incoming alerts, correlate with threat intel, and determine severity and response priority.',
    icon: <ShieldAlert className="w-6 h-6" />,
    status: 'active',
    confidence: 94,
    executionCount: 487,
    avgResponseTime: 3.2,
    accentColor: 'blue',
    accentBg: 'bg-blue-500/20',
    accentBorder: 'border-blue-500/30',
    accentText: 'text-blue-400',
    progressColor: 'from-blue-500 to-blue-400',
  },
  {
    id: 'incident_investigation',
    name: 'Incident Investigation',
    description:
      'Deep-dive analysis of security incidents, including timeline reconstruction, IOC extraction, and impact assessment.',
    icon: <SearchCode className="w-6 h-6" />,
    status: 'active',
    confidence: 91,
    executionCount: 234,
    avgResponseTime: 8.7,
    accentColor: 'green',
    accentBg: 'bg-emerald-500/20',
    accentBorder: 'border-emerald-500/30',
    accentText: 'text-emerald-400',
    progressColor: 'from-emerald-500 to-emerald-400',
  },
  {
    id: 'threat_hunting',
    name: 'Proactive Threat Hunting',
    description:
      'Search for hidden threats using MITRE ATT&CK patterns, behavioral analysis, and anomaly detection.',
    icon: <Crosshair className="w-6 h-6" />,
    status: 'active',
    confidence: 88,
    executionCount: 156,
    avgResponseTime: 12.4,
    accentColor: 'purple',
    accentBg: 'bg-purple-500/20',
    accentBorder: 'border-purple-500/30',
    accentText: 'text-purple-400',
    progressColor: 'from-purple-500 to-purple-400',
  },
  {
    id: 'access_review',
    name: 'Access & Identity Review',
    description:
      'Audit user permissions, detect stale accounts, review privileged access, and validate IAM policies.',
    icon: <UserCheck className="w-6 h-6" />,
    status: 'idle',
    confidence: 92,
    executionCount: 312,
    avgResponseTime: 5.1,
    accentColor: 'orange',
    accentBg: 'bg-orange-500/20',
    accentBorder: 'border-orange-500/30',
    accentText: 'text-orange-400',
    progressColor: 'from-orange-500 to-orange-400',
  },
  {
    id: 'compliance_check',
    name: 'Compliance & Posture Check',
    description:
      'Verify security controls, check configurations against CIS benchmarks, and generate compliance reports.',
    icon: <ClipboardCheck className="w-6 h-6" />,
    status: 'active',
    confidence: 86,
    executionCount: 198,
    avgResponseTime: 14.8,
    accentColor: 'cyan',
    accentBg: 'bg-cyan-500/20',
    accentBorder: 'border-cyan-500/30',
    accentText: 'text-cyan-400',
    progressColor: 'from-cyan-500 to-cyan-400',
  },
];

const RECENT_EXECUTIONS: SkillExecution[] = [
  {
    id: 'exec-001',
    skillName: 'Alert Triage & Enrichment',
    skillId: 'alert_triage',
    timestamp: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    duration: 3.1,
    result: 'success',
    trigger: 'automated',
  },
  {
    id: 'exec-002',
    skillName: 'Incident Investigation',
    skillId: 'incident_investigation',
    timestamp: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    duration: 9.4,
    result: 'escalated',
    trigger: 'automated',
  },
  {
    id: 'exec-003',
    skillName: 'Proactive Threat Hunting',
    skillId: 'threat_hunting',
    timestamp: new Date(Date.now() - 28 * 60 * 1000).toISOString(),
    duration: 11.2,
    result: 'success',
    trigger: 'manual',
  },
  {
    id: 'exec-004',
    skillName: 'Access & Identity Review',
    skillId: 'access_review',
    timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    duration: 5.8,
    result: 'success',
    trigger: 'automated',
  },
  {
    id: 'exec-005',
    skillName: 'Alert Triage & Enrichment',
    skillId: 'alert_triage',
    timestamp: new Date(Date.now() - 58 * 60 * 1000).toISOString(),
    duration: 2.9,
    result: 'partial',
    trigger: 'automated',
  },
  {
    id: 'exec-006',
    skillName: 'Compliance & Posture Check',
    skillId: 'compliance_check',
    timestamp: new Date(Date.now() - 72 * 60 * 1000).toISOString(),
    duration: 15.3,
    result: 'success',
    trigger: 'manual',
  },
  {
    id: 'exec-007',
    skillName: 'Incident Investigation',
    skillId: 'incident_investigation',
    timestamp: new Date(Date.now() - 95 * 60 * 1000).toISOString(),
    duration: 7.6,
    result: 'success',
    trigger: 'automated',
  },
];

// ---- Helper Functions ----

function getStatusBadge(status: SkillStatus) {
  switch (status) {
    case 'active':
      return (
        <span className="badge badge-success flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
          Active
        </span>
      );
    case 'idle':
      return (
        <span className="badge badge-warning flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
          Idle
        </span>
      );
    case 'disabled':
      return (
        <span className="badge bg-slate-600/30 text-slate-500 border border-slate-600/30 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-slate-500 rounded-full" />
          Disabled
        </span>
      );
  }
}

function getResultBadge(result: ExecutionResult) {
  switch (result) {
    case 'success':
      return (
        <span className="badge badge-success flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Success
        </span>
      );
    case 'escalated':
      return (
        <span className="badge badge-warning flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Escalated
        </span>
      );
    case 'partial':
      return (
        <span className="badge badge-primary flex items-center gap-1">
          <Activity className="w-3 h-3" />
          Partial
        </span>
      );
  }
}

function getTriggerBadge(trigger: ExecutionTrigger) {
  switch (trigger) {
    case 'automated':
      return (
        <span className="text-xs text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-2 py-0.5 flex items-center gap-1">
          <Zap className="w-3 h-3" />
          Auto
        </span>
      );
    case 'manual':
      return (
        <span className="text-xs text-slate-400 bg-slate-500/10 border border-slate-500/20 rounded-full px-2 py-0.5 flex items-center gap-1">
          <Play className="w-3 h-3" />
          Manual
        </span>
      );
  }
}

function getSkillAccentForExecution(skillId: string): string {
  const skill = SKILLS.find(s => s.id === skillId);
  return skill?.accentText ?? 'text-slate-400';
}

function formatTimeAgo(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ---- Component ----

export function SkillsPanel() {
  const [skills] = useState<SkillData[]>(SKILLS);
  const [executions] = useState<SkillExecution[]>(RECENT_EXECUTIONS);

  // Computed summary stats
  const totalExecutions = skills.reduce((sum, s) => sum + s.executionCount, 0);
  const avgConfidence = Math.round(
    skills.reduce((sum, s) => sum + s.confidence, 0) / skills.length,
  );
  const autoResolvedPct = 73;
  const topSkill = skills.reduce((top, s) => (s.executionCount > top.executionCount ? s : top));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ---- Header ---- */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              Agent Skills
              <Sparkles className="w-5 h-5 text-purple-400" />
            </h2>
            <p className="text-sm text-slate-400">
              Named investigation capabilities powering autonomous security operations
            </p>
          </div>
        </div>

        {/* ---- Summary Stats ---- */}
        <div className="grid grid-cols-4 gap-4">
          <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-primary-400" />
              <span className="text-xs text-slate-400">Total Executions</span>
            </div>
            <div className="text-2xl font-bold text-white">{totalExecutions.toLocaleString()}</div>
          </div>
          <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-slate-400">Avg Confidence</span>
            </div>
            <div className="text-2xl font-bold text-emerald-400">{avgConfidence}%</div>
          </div>
          <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-slate-400">Auto-Resolved</span>
            </div>
            <div className="text-2xl font-bold text-cyan-400">{autoResolvedPct}%</div>
          </div>
          <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpRight className="w-4 h-4 text-orange-400" />
              <span className="text-xs text-slate-400">Top Skill</span>
            </div>
            <div className="text-lg font-bold text-orange-400 truncate" title={topSkill.name}>
              {topSkill.name.split(' ')[0]}
            </div>
            <div className="text-xs text-slate-500">{topSkill.executionCount} runs</div>
          </div>
        </div>
      </div>

      {/* ---- Skills Grid ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {skills.map((skill, index) => (
          <div
            key={skill.id}
            className="glass rounded-2xl p-5 hover:border-slate-600/50 transition-all duration-300 group animate-slide-up"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            {/* Skill Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl ${skill.accentBg} border ${skill.accentBorder} flex items-center justify-center ${skill.accentText}`}
                >
                  {skill.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white leading-tight">{skill.name}</h3>
                </div>
              </div>
              {getStatusBadge(skill.status)}
            </div>

            {/* Description */}
            <p className="text-xs text-slate-400 mb-4 leading-relaxed line-clamp-2">
              {skill.description}
            </p>

            {/* Confidence Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-500">Confidence</span>
                <span className={`text-xs font-semibold ${skill.accentText}`}>
                  {skill.confidence}%
                </span>
              </div>
              <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${skill.progressColor} rounded-full transition-all duration-700 ease-out`}
                  style={{ width: `${skill.confidence}%` }}
                />
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700/30">
                <div className="text-xs text-slate-500 mb-0.5">Executions</div>
                <div className="text-sm font-bold text-white">{skill.executionCount}</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700/30">
                <div className="text-xs text-slate-500 mb-0.5">Avg Time</div>
                <div className="text-sm font-bold text-white flex items-center gap-1">
                  <Clock className="w-3 h-3 text-slate-500" />
                  {skill.avgResponseTime}s
                </div>
              </div>
            </div>

            {/* Run Button */}
            <button
              className={`w-full btn ${skill.accentBg} ${skill.accentText} border ${skill.accentBorder} hover:brightness-125 flex items-center justify-center gap-2 text-sm font-medium transition-all duration-200`}
            >
              <Play className="w-4 h-4" />
              Run Skill
            </button>
          </div>
        ))}
      </div>

      {/* ---- Recent Skill Executions ---- */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-primary-500/20 border border-primary-500/30 flex items-center justify-center">
            <Activity className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Recent Skill Executions</h3>
            <p className="text-xs text-slate-500">Last {executions.length} investigation runs</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider pb-3 pr-4">
                  Skill
                </th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider pb-3 pr-4">
                  Time
                </th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider pb-3 pr-4">
                  Duration
                </th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider pb-3 pr-4">
                  Result
                </th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider pb-3">
                  Trigger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {executions.map(exec => (
                <tr key={exec.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="py-3 pr-4">
                    <span
                      className={`text-sm font-medium ${getSkillAccentForExecution(exec.skillId)}`}
                    >
                      {exec.skillName}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-xs text-slate-400">{formatTimeAgo(exec.timestamp)}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-xs text-slate-300 font-mono">{exec.duration}s</span>
                  </td>
                  <td className="py-3 pr-4">{getResultBadge(exec.result)}</td>
                  <td className="py-3">{getTriggerBadge(exec.trigger)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
